import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import i18n from '../i18n';
import { isNative } from '../lib/native/platform';
import { registerPushForUser } from '../lib/native/push';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// V31: classify auth failures into safe, actionable outcomes instead of a generic "Login failed".
// User-facing text stays security-safe (never reveals whether an account exists). Dev console logs
// method/host/path/status ONLY — never email, password, token, cookie or Authorization header.
function classifyAuthError(error, url, fallback) {
  const resp = error?.response;
  const status = resp?.status;
  if (process.env.NODE_ENV !== 'production') {
    try {
      const u = new URL(url);
      // eslint-disable-next-line no-console
      console.log(`[RVX][auth] POST ${u.host}${u.pathname} -> ${status || 'NETWORK_UNREACHABLE'}`);
    } catch (_) {}
  }
  if (!resp) {
    let host = '';
    try { host = new URL(url).host; } catch (_) {}
    const devHost = process.env.NODE_ENV !== 'production' && host ? ` at ${host}` : '';
    return {
      code: 'NETWORK_UNREACHABLE',
      error:
        `Cannot reach the server${devHost}. Check your connection, and that the backend is running ` +
        `and reachable on your network.`,
    };
  }
  const detail = resp.data?.detail;
  if (status === 401) return { code: 'INVALID_CREDENTIALS', error: detail || 'Invalid email or password.' };
  if (status === 403) return { code: 'AUTHORIZATION_FAILED', error: detail || 'This account is not permitted to sign in.' };
  if (status === 429) return { code: 'RATE_LIMITED', error: detail || 'Too many attempts. Please try again shortly.' };
  if (status === 422) return { code: 'CONFIGURATION_ERROR', error: 'The server did not accept the login request format.' };
  if (status >= 500) return { code: 'SERVER_ERROR', error: 'The server encountered an error. Please try again shortly.' };
  return { code: 'AUTH_FAILED', error: detail || fallback };
}

// Native-only: register this device for push once authenticated. No-op on web.
const maybeRegisterPush = () => {
  if (isNative()) {
    registerPushForUser(API).catch(() => {});
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('accessToken'));

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('accessToken');
    
    if (storedUser && storedToken) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      if (parsed.language) i18n.changeLanguage(parsed.language);
      maybeRegisterPush();
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { accessToken, refreshToken, user: userData, firstLoginRequired } = response.data;
      const fullUser = { ...userData, firstLoginRequired: !!firstLoginRequired };

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(fullUser));

      setUser(fullUser);
      setToken(accessToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      // Apply the user's saved language; if none, keep the current (browser/localStorage) choice.
      if (userData.language) {
        i18n.changeLanguage(userData.language);
      }
      maybeRegisterPush();

      return { success: true, firstLoginRequired: !!firstLoginRequired, user: fullUser };
    } catch (error) {
      const c = classifyAuthError(error, `${API}/auth/login`, 'Login failed');
      return { success: false, error: c.error, code: c.code };
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, { oldPassword, newPassword });
      // Clear the first-login gate so protected routes stop redirecting to reset.
      setUser((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, firstLoginRequired: false };
        localStorage.setItem('user', JSON.stringify(updated));
        return updated;
      });
      return { success: true };
    } catch (error) {
      const c = classifyAuthError(error, `${API}/auth/change-password`, 'Password change failed');
      return { success: false, error: c.error, code: c.code };
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
